import type { LogMeta } from './types'

const MAX_DEPTH = 10
const MIN_PARTIAL_LENGTH = 12
const MAX_STAR_RUN = 8

export const SECRET_KEY_PATTERN =
  /password|passwd|secret|token|apikey|api[_-]key|authorization|credential|bearer|x[_-]?api[_-]?key|priv(?:ate)?[_-]?key|client[_-]?secret|refresh[_-]?token|access[_-]?token|cookie/i

export const SECRET_VALUE_PATTERNS: ReadonlyArray<RegExp> = [
  // JWT (three base64url segments)
  /\beyJ[\w-]{8,}\.[\w-]{8,}\.[\w-]{8,}\b/g,
  // AWS Access Key ID
  /\b(?:AKIA|ASIA)[0-9A-Z]{16}\b/g,
  // Stripe live/test keys (sk_, rk_, pk_)
  /\b(?:sk|rk|pk)_(?:live|test)_[A-Za-z0-9]{16,}\b/g,
  // GitHub tokens (ghp_, ghs_, gho_, ghu_, ghr_)
  /\bgh[pousr]_[A-Za-z0-9]{36,}\b/g,
  // Slack tokens
  /\bxox[abprs]-[\w-]{10,}\b/g,
  // Google API key
  /\bAIza[\w-]{35}\b/g,
  // Twilio API key SID
  /\bSK[a-f0-9]{32}\b/g,
  // Bearer header token (mask the token only, leave the "Bearer " word)
  /(?<=\bBearer\s+)[\w.~+/=-]{20,}/g,
  // Basic auth header (mask the credential only, leave the "Basic " word)
  /(?<=\bBasic\s+)[A-Za-z0-9+/=]{16,}/g
]

export function mask(value: string): string {
  if (value.length < MIN_PARTIAL_LENGTH) {
    return '***'
  }
  const head = value.slice(0, 4)
  const tail = value.slice(-4)
  const middleLen = Math.min(value.length - 8, MAX_STAR_RUN)
  return `${head}${'*'.repeat(middleLen)}${tail}`
}

export function redactString(input: string): string {
  if (!input) return input
  let out = input
  for (const pattern of SECRET_VALUE_PATTERNS) {
    out = out.replace(pattern, (match) => mask(match))
  }
  return out
}

export function redactError(err: unknown): unknown {
  if (!(err instanceof Error)) return err

  const newMessage = redactString(err.message)
  const newStack = err.stack ? redactString(err.stack) : err.stack

  if (newMessage === err.message && newStack === err.stack) {
    return err
  }

  const cloned = new Error(newMessage)
  cloned.name = err.name
  if (newStack) cloned.stack = newStack
  for (const key of Object.keys(err)) {
    if (key !== 'message' && key !== 'stack' && key !== 'name') {
      ;(cloned as unknown as Record<string, unknown>)[key] = (err as unknown as Record<string, unknown>)[key]
    }
  }
  return cloned
}

function redactValue(value: unknown, forceMask: boolean, seen: WeakSet<object>, depth: number): unknown {
  if (depth > MAX_DEPTH) return '[REDACTED:DEPTH]'
  if (value === null || value === undefined) return value

  if (typeof value === 'string') {
    return forceMask ? mask(value) : redactString(value)
  }

  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return forceMask ? '[REDACTED]' : value
  }

  if (typeof value !== 'object') {
    return value
  }

  if (value instanceof Error) {
    return redactError(value)
  }

  if (value instanceof Date || value instanceof RegExp) {
    return value
  }

  if (seen.has(value)) return '[Circular]'
  seen.add(value)

  if (Array.isArray(value)) {
    return value.map((v) => redactValue(v, forceMask, seen, depth + 1))
  }

  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    const childForce = forceMask || SECRET_KEY_PATTERN.test(k)
    out[k] = redactValue(v, childForce, seen, depth + 1)
  }
  return out
}

export function redactMeta(meta?: LogMeta): LogMeta | undefined {
  if (!meta) return meta
  const seen = new WeakSet<object>()
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(meta)) {
    // Internal flags pass through untouched (sentry, index)
    if (k === 'sentry' || k === 'index') {
      out[k] = v
      continue
    }
    if (k === 'error') {
      out.error = redactError(v)
      continue
    }
    const forceMask = SECRET_KEY_PATTERN.test(k)
    out[k] = redactValue(v, forceMask, seen, 1)
  }
  return out as LogMeta
}
